import type { Blog } from '@eliadmin/core/src/api/blogs/blogs-types';
import type { DailyDeal } from '@eliadmin/core/src/api/daily-deals/daily-deals-api';
import type { TDynamicData } from '@eliadmin/core/src/api/dynamics/dynamics-api';
import type { fetchIconsWithText } from '@eliadmin/core/src/api/general/general-api';
import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';
import type { Product } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';
import { buildProductsQuery } from '@eliadmin/core/src/utils/product-utils';

export type HomepageSectionData = {
  'top-products': Product[];
  'best-products': Product[];
  deals: DailyDeal[];
  categories: CategoriesEntity[];
  blogs: Blog[];
  stores: TDynamicData[];
  icons: Awaited<ReturnType<typeof fetchIconsWithText>>['data'];
};
export type HomepageSection = keyof HomepageSectionData;
export type HomepageSectionResponse = {
  [K in HomepageSection]: { section: K; data: HomepageSectionData[K] };
}[HomepageSection];

export const HOMEPAGE_SECTIONS: readonly HomepageSection[] = [
  'top-products',
  'best-products',
  'deals',
  'categories',
  'blogs',
  'stores',
  'icons',
];
export const HOMEPAGE_SECTION_STALE_MS = 5 * 60 * 1000;

export function isHomepageSection(value: string): value is HomepageSection {
  return HOMEPAGE_SECTIONS.some(section => section === value);
}

export function homepageProductsQuery(section: 'top-products' | 'best-products') {
  return buildProductsQuery({
    results: 8,
    page: 1,
    order: 'desc',
    orderBy: section === 'top-products' ? 'views' : 'sells',
    // Product cards/variants still receive the existing attribute contract.
    showAttributes: true,
  });
}

export function isActiveHomepageDeal(deal: DailyDeal | undefined, now = Date.now()) {
  return Boolean(
    deal?.items[0]?.product?.quantity &&
    deal.items[0].product.quantity > 0 &&
    deal.starts_at &&
    deal.ends_at &&
    Date.parse(deal.starts_at) <= now &&
    Date.parse(deal.ends_at) >= now,
  );
}

export function homepageSectionKey(section: HomepageSection, locale: string, customerId?: number) {
  return ['mediatip-homepage-section-v1', locale, section, customerId ?? 'guest'] as const;
}
